import React, { useEffect, useMemo, useState } from 'react';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import IconAdd from '@mui/icons-material/Add';
import ShowChartOutlinedIcon from '@mui/icons-material/ShowChartOutlined';
import styles from './growthCurve.module.css';
import parts from '../moduleCard/moduleParts.module.css';
import CustomButton from '../../../../components/buttons/CustomButton';
import ModuleCard from '../moduleCard/ModuleCard';
import ModuleEmptyState from '../moduleCard/ModuleEmptyState';
import ModulePdfButton from '../moduleCard/ModulePdfButton';
import ConfirmPopup from '../../../../popup/ConfirmPopup/ConfirmPopup';
import GrowthMeasurementPopup from '../../../../popup/GrowthMeasurementPopup/GrowthMeasurementPopup';
import HiddenReport from '../../../../components/documents-templates/report/HiddenReport';
import useReportExport from '../../../../components/documents-templates/report/useReportExport';
import ModuleReportTemplate from '../../../../components/documents-templates/report/ModuleReportTemplate';
import GrowthChartPanel from './GrowthChartPanel';
import GrowthHistoryTable from './GrowthHistoryTable';
import { DataProviderInstance } from '../../../../api/DataProvider';
import { MODULE_KEYS } from '../../modules/appointmentModules';
import { REPORT_SECTIONS, buildGrowthCurveJob } from '../../modules/reportSections';
import { GROWTH_INDICATORS, WHO_ATTRIBUTION, missingPatientInfo, sexFromGender } from '../../modules/growth/growthIndicators';
import { cranialAges, growthStatsList, indicatorSeries, indicatorsWithData } from '../../modules/growth/growthSeries';

const SECTION = REPORT_SECTIONS[MODULE_KEYS.GROWTH_CURVE];

/**
 * Módulo "Curva de crescimento": medições datadas de peso, comprimento/altura
 * e perímetro cefálico sobre as curvas da OMS (peso, comprimento/altura, PC e
 * IMC por idade), com percentil e escore-z de cada medição.
 *
 * `data`: medições do paciente (todas, de qualquer atendimento).
 * `cranial`: medições da assimetria craniana; só as datas entram, como
 * marcadores no gráfico do perímetro cefálico (a ficha craniana não mede PC).
 * `birthday`/`gender`: do cadastro, base da idade e da curva por sexo.
 * `enabled`: false na aba Saúde do paciente (só leitura, mas ainda exporta).
 */
const GrowthCurve = ({
  data,
  cranial,
  patientId,
  appointmentId,
  appointmentDate,
  birthday,
  gender,
  enabled,
  collapsible = false,
  onUpdated,
  patientName,
  professionalId,
  companyConfig,
}) => {
  const { DeletePatientGrowthMeasurement } = DataProviderInstance();
  const [history, setHistory] = useState(Array.isArray(data) ? data : []);
  const [indicatorKey, setIndicatorKey] = useState(null);
  const [mode, setMode] = useState('percentile');
  const [popupEntry, setPopupEntry] = useState(undefined);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    setHistory(Array.isArray(data) ? data : []);
  }, [data]);

  const statsList = useMemo(() => growthStatsList(history, birthday, gender), [history, birthday, gender]);
  const available = indicatorsWithData(statsList);
  const indicator = GROWTH_INDICATORS.find((item) => item.key === indicatorKey && available.includes(item)) || available[0];
  const series = indicator ? indicatorSeries(statsList, indicator) : null;
  const markers = useMemo(() => cranialAges(cranial, birthday), [cranial, birthday]);
  const missing = missingPatientInfo(birthday, gender);
  const sex = sexFromGender(gender);
  const hasCranial = Array.isArray(cranial) && cranial.length > 0;

  const report = useReportExport({
    patientName,
    professionalId,
    companyConfig,
    reportType: SECTION.reportType,
    prepare: () => buildGrowthCurveJob(history, { birthday, gender }, cranial),
  });

  function popupHideHandler(needsReload) {
    setPopupEntry(undefined);
    if (needsReload && onUpdated) onUpdated();
  }

  function confirmDelete() {
    const entry = deleting;
    setDeleting(null);
    DeletePatientGrowthMeasurement(entry.id, (response) => {
      if (response && response.status === 'SUCCESS' && onUpdated) onUpdated();
    });
  }

  const actions = (
    <>
      {history.length > 0 && <ModulePdfButton report={report} />}
      {enabled && (
        <CustomButton
          variant="contained"
          style="primary"
          label="Nova medição"
          icon={<IconAdd />}
          onClick={() => setPopupEntry(null)}
        />
      )}
    </>
  );

  return (
    <>
      <ModuleCard
        accent="teal"
        title="Curva de crescimento"
        icon={<ShowChartOutlinedIcon />}
        description="Peso, comprimento/altura e perímetro cefálico nas curvas da OMS"
        actions={actions}
        collapsible={collapsible}
      >
        <div className={styles.content}>
          {missing.length > 0 && (
            <div className={styles.warning}>
              Para calcular idade, percentil e curva, complete no cadastro do paciente: {missing.join(' e ')}.
            </div>
          )}

          {!indicator ? (
            <ModuleEmptyState icon={<ShowChartOutlinedIcon />} text="Nenhuma medição registrada." />
          ) : (
            <>
              <div className={styles.toolbar}>
                <ToggleButtonGroup size="small" exclusive value={indicator.key} onChange={(e, value) => value && setIndicatorKey(value)}>
                  {available.map((item) => (
                    <ToggleButton key={item.key} value={item.key} className={parts.toggleButton}>{item.label}</ToggleButton>
                  ))}
                </ToggleButtonGroup>
                <div className={styles.controls}>
                  <ToggleButtonGroup size="small" exclusive value={mode} onChange={(e, value) => value && setMode(value)}>
                    <ToggleButton value="percentile" className={parts.toggleButton}>Percentis</ToggleButton>
                    <ToggleButton value="z" className={parts.toggleButton}>Escore-z</ToggleButton>
                  </ToggleButtonGroup>
                </div>
              </div>

              <GrowthChartPanel series={series} sex={sex} mode={mode} markers={indicator.key === 'head' ? markers : []} />

              {indicator.key === 'head' && hasCranial && (
                <span className={parts.muted}>
                  Os triângulos marcam as datas da assimetria craniana. A ficha craniana não registra perímetro cefálico: o PC desta curva vem das medições deste módulo.
                </span>
              )}

              <GrowthHistoryTable statsList={statsList} enabled={enabled} onEdit={setPopupEntry} onDelete={setDeleting} />
              <span className={styles.attribution}>{WHO_ATTRIBUTION}</span>
            </>
          )}
        </div>
      </ModuleCard>

      {report.job && (
        <HiddenReport>
          <ModuleReportTemplate ref={report.ref} section={SECTION} job={report.job} {...report.paperProps} />
        </HiddenReport>
      )}

      {popupEntry !== undefined && (
        <GrowthMeasurementPopup
          patientId={patientId}
          appointmentId={appointmentId}
          appointmentDate={appointmentDate}
          entry={popupEntry}
          birthday={birthday}
          gender={gender}
          OnHide={popupHideHandler}
        />
      )}

      {deleting && (
        <ConfirmPopup
          Visibility={true}
          Title="Confirmar exclusão"
          Description="Tem certeza que deseja excluir esta medição?"
          ConfirmLabel="Excluir"
          OnConfirm={confirmDelete}
          OnHide={() => setDeleting(null)}
        />
      )}
    </>
  );
};

export default GrowthCurve;
